/**
 * Bid role administration (P4) — grant/revoke the construction roles.
 *
 * Until this screen existed roles were reachable only through the API or raw SQL, so a deployment
 * could not actually administer the feature it had shipped.
 *
 * Two things it deliberately does:
 *  - **Invalidates the capability cache after every change.** `useBidCapabilities` caches per
 *    session, so without this an admin who grants themselves a role would keep the old capabilities
 *    (and the old money visibility) until a full page reload.
 *  - **Says what a role means.** "Superintendent" is not self-evidently "never sees a dollar
 *    value", and that is the one property this whole phase exists to guarantee.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import ToastService from '@frontend/AppSettings/services/ToastService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import {
  BID_ROLES,
  BidRole,
  BidRoleAssignment,
  ROLE_SUMMARY,
  grantBidRole,
  listBidRoleAssignments,
  revokeBidRole,
} from './bidRolesService';
import { invalidateBidCapabilities } from '../useBidCapabilities';
import './BidAdminPage.css';

interface Employee {
  employeeid: string;
  name: string;
}

const RolesPanel: React.FC = () => {
  const [assignments, setAssignments] = useState<BidRoleAssignment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole] = useState<BidRole>('estimator');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<BidRoleAssignment | null>(null);
  const [employeeError, setEmployeeError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setAssignments(await listBidRoleAssignments());
    } catch (err: any) {
      setError(err?.message || 'Could not load role assignments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void EmployeeService.getEmployees()
      .then((res: any) => {
        if (cancelled) return;
        const rows = (res?.data ?? res ?? []) as any[];
        setEmployees(
          rows
            .map((e) => ({
              employeeid: String(e.employeeid ?? e.EmployeeID ?? ''),
              name: [e.firstname ?? e.FirstName, e.lastname ?? e.LastName]
                .filter(Boolean)
                .join(' ')
                .trim(),
            }))
            .filter((e) => e.employeeid),
        );
      })
      // Granting needs the picker, so a failure here is a dead end, not a graceful degrade — the
      // table still resolves ids to names, but the form cannot be used. Say so rather than
      // presenting an empty dropdown with no explanation.
      .catch(() => {
        if (!cancelled) {
          setEmployees([]);
          setEmployeeError('Could not load the employee list — reload to grant a role.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const nameFor = useMemo(() => {
    const byId = new Map(employees.map((e) => [e.employeeid, e.name]));
    return (id: string): string => byId.get(id) || id;
  }, [employees]);

  /** Any role change can alter the CALLER's own capabilities, so drop the cached set. */
  const afterChange = async (): Promise<void> => {
    invalidateBidCapabilities();
    await load();
  };

  const handleGrant = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!employeeId) {
      ToastService.error('Choose an employee first.');
      return;
    }
    setBusy(true);
    try {
      await grantBidRole(employeeId, role);
      ToastService.success(`Granted ${role.replace(/_/g, ' ')} to ${nameFor(employeeId)}`);
      setEmployeeId('');
      await afterChange();
    } catch (err: any) {
      ToastService.error(err?.message || 'Could not grant that role.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (): Promise<void> => {
    if (!pendingRevoke) return;
    const target = pendingRevoke;
    setPendingRevoke(null);
    try {
      await revokeBidRole(target.employeeid, target.role);
      ToastService.success(`Revoked ${target.role.replace(/_/g, ' ')} from ${nameFor(target.employeeid)}`);
      await afterChange();
    } catch (err: any) {
      ToastService.error(err?.message || 'Could not revoke that role.');
    }
  };

  /** Revoking the last bid_administrator, or your own, is recoverable only via a system admin. */
  const revokeWarning = useMemo(() => {
    if (!pendingRevoke) return '';
    const who = nameFor(pendingRevoke.employeeid);
    const role = pendingRevoke.role.replace(/_/g, ' ');
    const base = `Revoke "${role}" from ${who}? They lose the access it granted immediately.`;
    if (pendingRevoke.role !== 'bid_administrator') return base;
    const admins = assignments.filter((a) => a.role === 'bid_administrator').length;
    if (admins <= 1) {
      return `${base}\n\nThis is the LAST bid administrator. Afterwards only a system administrator will be able to assign bid roles.`;
    }
    return base;
  }, [pendingRevoke, assignments, nameFor]);

  return (
    <div className="bid-admin-roles" data-testid="bid-roles-panel">
      <p className="bid-admin-import-help">
        Bid roles control who can see dollar values and who can edit bids. They are separate from
        the app&apos;s permission level — a system administrator can manage reference data and
        assign roles, but does not see bid financials without a role that grants it.
      </p>

      <form className="bid-admin-import-controls" onSubmit={handleGrant}>
        <label htmlFor="role-employee">Employee</label>
        <select
          id="role-employee"
          className="bid-admin-input"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
        >
          <option value="">Choose…</option>
          {employees.map((e) => (
            <option key={e.employeeid} value={e.employeeid}>
              {e.name || e.employeeid}
            </option>
          ))}
        </select>

        <label htmlFor="role-name">Role</label>
        <select
          id="role-name"
          className="bid-admin-input"
          value={role}
          onChange={(e) => setRole(e.target.value as BidRole)}
        >
          {BID_ROLES.map((r) => (
            <option key={r} value={r}>
              {r.replace(/_/g, ' ')}
            </option>
          ))}
        </select>

        <button type="submit" className="bid-admin-btn is-primary" disabled={busy}>
          <FontAwesomeIcon icon={faPlus} /> Grant role
        </button>
      </form>

      <p className="bid-admin-import-help" data-testid="bid-role-summary">
        <strong>{role.replace(/_/g, ' ')}:</strong> {ROLE_SUMMARY[role]}
      </p>

      {(error || employeeError) && (
        <div className="bid-admin-error" role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error ?? employeeError}
        </div>
      )}

      {loading ? (
        <p className="bid-admin-empty">Loading role assignments…</p>
      ) : assignments.length === 0 ? (
        <p className="bid-admin-empty">
          No bid roles assigned yet. Nobody can see bid financials or edit bids until a role is
          granted.
        </p>
      ) : (
        <table className="bid-admin-table" data-testid="bid-roles-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Role</th>
              <th>What it grants</th>
              <th>Granted by</th>
              <th>Granted</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id}>
                <td>{nameFor(a.employeeid)}</td>
                <td>
                  <span className="bid-admin-badge is-on">{a.role.replace(/_/g, ' ')}</span>
                </td>
                <td>{ROLE_SUMMARY[a.role] ?? '—'}</td>
                {/* the point of resolving the grantor server-side is attribution — show it */}
                <td>{a.granted_by ? nameFor(a.granted_by) : '—'}</td>
                <td>{a.granted_at ? String(a.granted_at).slice(0, 10) : '—'}</td>
                <td className="bid-admin-row-actions">
                  <button
                    type="button"
                    className="bid-admin-btn is-danger"
                    onClick={() => setPendingRevoke(a)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmationModal
        isOpen={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        onConfirm={handleRevoke}
        message={revokeWarning}
        confirmLabel="Revoke"
      />
    </div>
  );
};

export default RolesPanel;
